import React from 'react';
import { PlayerWithStats, StatDefinition } from '@repo/types';
import { ClubNameType } from '../../../fixtures/contracts';
import { Image } from '@ui/common/components/Image';

export type PlayerCardProps = {
  player: PlayerWithStats;
  stats: StatDefinition[];
  showPhoto?: boolean;
  showPosition?: boolean;
  showTeam?: boolean;
  clubNameType?: ClubNameType;
};

const getClubDisplayName = (player: PlayerWithStats, clubNameType?: ClubNameType): string => {
  const club = player.team?.club;
  if (!club) {
    return player.team?.fullName || '';
  }

  switch (clubNameType) {
    case ClubNameType.ThreeLetterName:
      return club.threeLetterName || club.shortName || club.fullName || '';
    case ClubNameType.ShortName:
      return club.shortName || club.fullName || '';
    case ClubNameType.FullName:
      return club.fullName || '';
    case ClubNameType.NickName:
      return club.nickName || club.shortName || club.fullName || '';
    default:
      return club.shortName || club.fullName || '';
  }
};

export const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  stats,
  showPhoto = true,
  showPosition = true,
  showTeam = true,
  clubNameType = ClubNameType.ShortName,
}) => {
  const initials = player.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const clubDisplayName = getClubDisplayName(player, clubNameType);

  return (
    <div className="player-card">
      {showPhoto && (
        <div className="player-card__media">
          <div className="player-card__avatar">
            <span className="player-card__initials">{initials}</span>
            <div className="player-card__badge-over" />
          </div>
        </div>
      )}

      <div className="player-card__header">
        <h3 className="player-card__name small-text">{player.name}</h3>
        {showPosition && player.position && <span className="player-card__position text-3xs">{player.position}</span>}
        {showTeam && player.team && (
          <div className="player-card__team">
            {player.team.logo && <Image className="player-card__team-logo" src={player.team.logo} alt="" />}
            <span className="player-card__team-name small-text">{clubDisplayName}</span>
          </div>
        )}
        {stats.length > 0 && (
          <div className="player-card__stats small-text">
            {stats.map((stat, index) => (
              <span key={stat.id} className="player-card__stat">
                <span className="player-card__stat-value">{player.stats[stat.id] ?? 0}</span>{' '}
                <span className="player-card__stat-label">{stat.label}</span>
                {index < stats.length - 1 && ' · '}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
